import {
  ActionReducerMapBuilder,
  createSlice,
  isRejectedWithValue,
  PayloadAction,
} from '@reduxjs/toolkit';
import {
  addBlogArticleDiscussion,
  addDiscussionComment,
  editBlogDiscussion,
  editDiscussionComment,
  fetchFullDiscussionPage,
  getDiscussionComments,
  getDiscussionDetailsById,
} from '../actions/discussionActions';
import { getBlogDiscussionById } from '../../blogs/actions/blogPageActions';
import {
  adminToggleDeleteComment,
  adminToggleDeleteDiscussion,
} from '../../admin/actions/adminCommentActions';

const initialState: DiscussionStateType = {
  discussionDetails: {
    id: 0,
    title: '',
    content: '',
    content_slug: '',
    content_title: '',
    content_id: 0,
    content_type: 'blog',
    user: {
      username: '',
    },
    created_on: '',
    comment_count: 0,
  },
  commentDetails: [],
  pagination: {
    next: null,
    previous: null,
    count: 0,
  },
  loadingMoreComments: false,
  nextPage: 1,
  postingComment: false,
  loadingInitialDiscussion: false,

  blogDiscussionsList: {
    count: 0,
    next: null,
    previous: null,
    results: [],
  },
};

const discussionSlice = createSlice({
  name: 'discussionDetailStateSlice',
  initialState,
  reducers: {
    resetDiscussionState: () => initialState,
  },

  extraReducers: builder => {
    discussionDetailsbyId(builder);
    discussionComments(builder);
    createDiscussionComment(builder);
    editDiscussionCommentReducer(builder);
    fetchFullDiscussionPageReducer(builder);
    editBlogDiscussionReducer(builder);
    addBlogArticleDiscussionReducer(builder);
    blogDiscussions(builder);
    adminModerationCases(builder);
  },
});

const discussionDetailsbyId = (
  builder: ActionReducerMapBuilder<DiscussionStateType>
) => {
  builder.addCase(
    getDiscussionDetailsById.fulfilled,
    (
      state: DiscussionStateType,
      action: PayloadAction<DiscussionDetailsType>
    ) => {
      state.discussionDetails = action.payload;
    }
  );

  builder.addCase(getDiscussionDetailsById.rejected, (state, action) => {
    if (isRejectedWithValue(action)) {
      console.error('Error fetching discussion details:', action.payload);
    }
  });
};

const discussionComments = (
  builder: ActionReducerMapBuilder<DiscussionStateType>
) => {
  builder.addCase(
    getDiscussionComments.pending,
    (state: DiscussionStateType) => {
      state.loadingMoreComments = true;
    }
  );
  builder.addCase(
    getDiscussionComments.fulfilled,
    (
      state: DiscussionStateType,
      action: PayloadAction<{
        results: CommentDetailsType[];
        next: string | null;
        previous: string | null;
        count: number;
        page: number;
      }>
    ) => {
      if (action.payload.page > 1) {
        state.commentDetails.push(...action.payload.results);
      } else {
        state.commentDetails = action.payload.results;
      }

      state.pagination = {
        next: action.payload.next,
        previous: action.payload.previous,
        count: action.payload.count,
      };
      state.loadingMoreComments = false;
      state.nextPage += 1;
    }
  );

  builder.addCase(
    getDiscussionComments.rejected,
    (state: DiscussionStateType) => {
      state.loadingMoreComments = false;
    }
  );
};

const createDiscussionComment = (
  builder: ActionReducerMapBuilder<DiscussionStateType>
) => {
  builder.addCase(addDiscussionComment.pending, state => {
    state.postingComment = true;
  });
  builder.addCase(
    addDiscussionComment.fulfilled,
    (state: DiscussionStateType, action: PayloadAction<CommentDetailsType>) => {
      const newComment = action.payload;
      if (newComment.parent != null) {
        const parentIndex = state.commentDetails.findIndex(c => c.id === newComment.parent);
        if (parentIndex !== -1) {
          const parent = state.commentDetails[parentIndex];
          state.commentDetails[parentIndex] = {
            ...parent,
            replies: [...(parent.replies || []), newComment],
          };
        }
      } else {
        state.commentDetails.push(newComment);
      }
      state.discussionDetails.comment_count += 1;
      state.postingComment = false;
    }
  );

  builder.addCase(
    addDiscussionComment.rejected,
    (state: DiscussionStateType, action) => {
      state.postingComment = false;
      if (action.payload) {
        console.error('Failed to add comment:', action.payload.detail);
      } else {
        console.error('Unexpected error:', action.error);
      }
    }
  );
};

const editDiscussionCommentReducer = (
  builder: ActionReducerMapBuilder<DiscussionStateType>
) => {
  builder.addCase(
    editDiscussionComment.fulfilled,
    (state: DiscussionStateType, action: PayloadAction<CommentDetailsType>) => {
      const updated = action.payload;
      if (updated.parent != null) {
        const parentIndex = state.commentDetails.findIndex(c => c.id === updated.parent);
        if (parentIndex !== -1) {
          const parent = state.commentDetails[parentIndex];
          state.commentDetails[parentIndex] = {
            ...parent,
            replies: (parent.replies || []).map(r => r.id === updated.id ? updated : r),
          };
        }
      } else {
        const commentIndex = state.commentDetails.findIndex(c => c.id === updated.id);
        if (commentIndex !== -1) {
          state.commentDetails[commentIndex] = updated;
        }
      }
    }
  );

  builder.addCase(
    editDiscussionComment.rejected,
    (state: DiscussionStateType, action) => {
      if (action.payload) {
        console.error('Failed to edit comment:', action.payload.detail);
      } else {
        console.error('Unexpected error:', action.error);
      }
    }
  );
};

const fetchFullDiscussionPageReducer = (
  builder: ActionReducerMapBuilder<DiscussionStateType>
) => {
  builder.addCase(fetchFullDiscussionPage.pending, state => {
    state.loadingInitialDiscussion = true;
  });
  builder.addCase(fetchFullDiscussionPage.fulfilled, state => {
    state.loadingInitialDiscussion = false;
  });
  builder.addCase(fetchFullDiscussionPage.rejected, state => {
    state.loadingInitialDiscussion = false;
    // You could add error handling here if the thunk was set up to reject
    // The individual actions within the thunk already handle their own rejection states.
  });
};

const editBlogDiscussionReducer = (
  builder: ActionReducerMapBuilder<DiscussionStateType>
) => {
  builder.addCase(
    editBlogDiscussion.fulfilled,
    (
      state: DiscussionStateType,
      action: PayloadAction<DiscussionDetailsType>
    ) => {
      // Update the discussion details with the edited content
      state.discussionDetails = action.payload;
    }
  );

  builder.addCase(
    editBlogDiscussion.rejected,
    (state: DiscussionStateType, action) => {
      if (action.payload) {
        console.error('Failed to edit discussion:', action.payload.detail);
      } else {
        console.error('Unexpected error:', action.error);
      }
    }
  );
};
const addBlogArticleDiscussionReducer = (
  builder: ActionReducerMapBuilder<DiscussionStateType>
) => {
  builder.addCase(addBlogArticleDiscussion.pending, (state) => {
    state.postingComment = true; 
  });

  builder.addCase(
    addBlogArticleDiscussion.fulfilled,
    (state, action: PayloadAction<{ newDiscussion: any; contentType: 'blog' | 'article' | 'clearing'; contentId: number }>) => {
      // ✅ BUG FIX: Reset the loading state
      state.postingComment = false; 
      
      const { newDiscussion, contentType } = action.payload;

      if (contentType === 'blog' && newDiscussion) {
        const discussionDetails: DiscussionDetailsType = {
          id: newDiscussion.id,
          title: newDiscussion.title,
          content: newDiscussion.content,
          content_slug: newDiscussion.content_slug || '',
          content_title: newDiscussion.content_title || '',
          content_id: newDiscussion.content_id || newDiscussion.blog || 0,
          content_type: 'blog',
          user: newDiscussion.user,
          created_on: newDiscussion.created_on,
          comment_count: 0,
        };

        state.blogDiscussionsList.results.unshift(discussionDetails);
        state.blogDiscussionsList.count += 1;
      }
    }
  );

  builder.addCase(addBlogArticleDiscussion.rejected, (state, action) => {
    state.postingComment = false;
    // ... error logging ...
  });
};

const blogDiscussions = (
  builder: ActionReducerMapBuilder<DiscussionStateType>
) => {
  builder.addCase(getBlogDiscussionById.pending, (state) => {
    // 💡 Suggestion: Use state.loadingList if you want to be more specific
    state.loadingInitialDiscussion = true;
  });

  builder.addCase(
    getBlogDiscussionById.fulfilled,
    (state, action: PayloadAction<BlogDiscussionsListSuccessType>) => {
      state.loadingInitialDiscussion = false;
      state.blogDiscussionsList = action.payload;
    }
  );

  builder.addCase(getBlogDiscussionById.rejected, (state) => {
    state.loadingInitialDiscussion = false;
  });
};


const adminModerationCases = (
  builder: ActionReducerMapBuilder<DiscussionStateType>
) => {
  builder.addCase(adminToggleDeleteComment.fulfilled, (state, action) => {
    const { id, is_deleted } = action.payload.comment;
    const topLevelIdx = state.commentDetails.findIndex(c => c.id === id);
    if (topLevelIdx !== -1) {
      // Parent comment: mark deleted/restored so replies stay visible
      state.commentDetails[topLevelIdx] = {
        ...state.commentDetails[topLevelIdx],
        is_deleted,
      };
      if (is_deleted && state.discussionDetails.comment_count > 0) {
        state.discussionDetails.comment_count -= 1;
      } else if (!is_deleted) {
        state.discussionDetails.comment_count += 1;
      }
    } else {
      // Reply: no children, just remove it from the parent's replies
      state.commentDetails.forEach(c => {
        if (c.replies) c.replies = c.replies.filter(r => r.id !== id);
      });
    }
  });

  builder.addCase(adminToggleDeleteDiscussion.fulfilled, (state, action) => {
    const { id, is_deleted } = action.payload.discussion;
    if (is_deleted && state.discussionDetails.id === id) {
      // Signal the page to navigate away by zeroing the id sentinel
      state.discussionDetails = { ...state.discussionDetails, id: -1 };
    }
  });
};

export const { resetDiscussionState } = discussionSlice.actions;

export default discussionSlice.reducer;
